import { FC, useMemo } from 'react';

import styled from '@emotion/styled';
import { OrganisationCountryPaymentSettings } from 'store/payment/paymentAPI';
import { PaymentType } from 'core';
import DOMPurify from 'dompurify';

import { BcRadio, SuiPanel } from '@britishcouncil/react-solas-ors3';
import { FormFields } from '@britishcouncil/react-forms';
import { IcoLock, Icon } from '@britishcouncil/react-solas';
import { OnlinePaymentDiscountAdvertMsg } from 'components/OnlinePaymentDiscountAdvertMsg';

export const SelectPaymentFields: FC<{
  fields: FormFields<{ paymentType: number }>;
  paymentSettings: OrganisationCountryPaymentSettings | undefined;
  fee: string;
  onlinePaymentFee: string;
  offlinePaymentTerms: string | undefined;
  taxInfo: string | undefined;
}> = ({ fields, paymentSettings, fee, onlinePaymentFee, offlinePaymentTerms = '', taxInfo }) => {
  const { onlinePayment, offlinePayments } = useMemo(
    () => ({
      onlinePayment: paymentSettings?.allowedPaymentDescriptions.find((x) => x.shortCode === 'ONLINECARD'),
      offlinePayments: paymentSettings?.allowedPaymentDescriptions.filter((x) => x.shortCode !== 'ONLINECARD') ?? [],
    }),
    [paymentSettings],
  );

  const showOnlinePaymentAdvert = fee !== onlinePaymentFee;

  return (
    <>
      {paymentSettings?.allowOnlinePayment && (
        <SuiPanel title="Online payment">
          <PaymentBox>
            <BcRadio
              name={fields.paymentType.name}
              value={fields.paymentType.value}
              onChange={(_) => fields.paymentType.onChange(PaymentType.OnlinePayment)}
              checkedValue={PaymentType.OnlinePayment}
              background="gray"
            >
              Pay online
            </BcRadio>
            <OnlinePaymentDescription
              description={onlinePayment?.description}
              fee={fee}
              onlinePaymentFee={onlinePaymentFee}
              taxInfo={taxInfo}
            />
            {showOnlinePaymentAdvert && <OnlinePaymentDiscountAdvertMsg countryId={paymentSettings?.countryId} />}
          </PaymentBox>
        </SuiPanel>
      )}

      {paymentSettings?.allowOfflinePayment && (
        <SuiPanel title="Offline payment">
          <PaymentBox>
            <BcRadio
              name={fields.paymentType.name}
              value={fields.paymentType.value}
              onChange={(_) => fields.paymentType.onChange(PaymentType.OfflinePayment)}
              checkedValue={PaymentType.OfflinePayment}
              background="gray"
            >
              Pay another way
            </BcRadio>
            <OfflinePaymentDescription
              paymentMethods={offlinePayments}
              fee={fee}
              terms={offlinePaymentTerms}
              taxInfo={taxInfo}
            />
          </PaymentBox>
        </SuiPanel>
      )}
    </>
  );
};

const OnlinePaymentDescription: FC<{
  description: string | undefined;
  fee: string;
  onlinePaymentFee: string;
  taxInfo: string | undefined;
}> = ({ description = '', fee, onlinePaymentFee, taxInfo }) => (
  <>
    <p>We accept Visa and Mastercard credit and debit cards.</p>
    <p>
      <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }} />
    </p>
    <p>
      <Icon size="small" fitted>
        <IcoLock />
      </Icon>{' '}
      You will be directed to a secure payment page.
    </p>
    <p>
      <strong>
        Fee:{' '}
        {fee == onlinePaymentFee ? (
          fee
        ) : (
          <>
            <s>{fee}</s> {onlinePaymentFee}
          </>
        )}
        {taxInfo}
      </strong>
    </p>
  </>
);

const OfflinePaymentDescription: FC<{
  paymentMethods: { header: string; description: string }[];
  fee: string;
  terms: string | undefined;
  taxInfo: string | undefined;
}> = ({ paymentMethods, fee, terms = '', taxInfo }) => (
  <>
    <p>
      <strong>If you choose 'Offline payment', registration is not complete until payment is received.</strong>
    </p>
    <p>Ways to pay:</p>
    {paymentMethods.map((item, i) => (
      <div key={i}>
        <p>
          <strong>{item.header}</strong>
        </p>
        <p>
          <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.description) }} />
        </p>
      </div>
    ))}
    <strong>
      <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(terms) }}></span>
    </strong>
    <p>
      <strong>
        Fee: {fee} {taxInfo}
      </strong>
    </p>
  </>
);

const PaymentBox = styled.div`
  padding: 1em;

  .radio {
    margin-bottom: 20px;
  }
`;
